import React from 'react';

import {
  formatAsCurrency,
  formatAsPartialCurrency,
  sanitizePartialCurrencyValue,
  updateCursorLocation,
} from './CurrencyInput.helpers';
import { useOrionUID } from '../../helpers/orionUID';
import { Icons } from '../icon/Icon';
import { Input } from '../input/Input';

// Using string & {} to get enum auto-complete while allowing any string
// eslint-disable-next-line @typescript-eslint/ban-types
export type Locale = 'en-GB' | 'de-DE' | (string & {});

export type Currency = {
  locale: Locale;
  currencySymbol?: boolean;
  minorUnits?: boolean;
};

type CurrencyInputProps = {
  label?: string;
  labelProps?: React.LabelHTMLAttributes<HTMLLabelElement>;
  description?: string;
  descriptionProps?: React.HTMLAttributes<HTMLSpanElement>;
  iconLeft?: (typeof Icons)[number];
  iconRight?: (typeof Icons)[number];
  error?: string;
  success?: string;
  wrapperClassName?: string;
  currency: Currency;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'>;

export const CurrencyInput = React.forwardRef<
  HTMLInputElement,
  CurrencyInputProps
>(({ currency, ...inputProps }, ref) => {
  const orionUID = useOrionUID();

  const value = inputProps.value
    ? formatAsCurrency(inputProps.value.toString(), currency)
    : inputProps.value;

  const defaultValue = inputProps.defaultValue
    ? formatAsCurrency(inputProps.defaultValue.toString(), currency)
    : inputProps.defaultValue;

  const onChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const sanitisedValue = sanitizePartialCurrencyValue(
      ev.target.value,
      currency,
    );
    const formattedCurrency = formatAsPartialCurrency(sanitisedValue, currency);
    ev.target.value = formattedCurrency;

    // update cursor location
    updateCursorLocation(ev, currency);

    inputProps.onChange && inputProps.onChange(ev);
  };

  const onBlur = (ev: React.FocusEvent<HTMLInputElement>) => {
    const sanitisedValue = sanitizePartialCurrencyValue(
      ev.target.value,
      currency,
    );
    ev.target.value = formatAsCurrency(sanitisedValue, currency);

    inputProps.onBlur && inputProps.onBlur(ev);
  };

  return (
    <Input
      id={orionUID}
      {...inputProps}
      onBlur={onBlur}
      onChange={onChange}
      defaultValue={defaultValue}
      value={value}
      type="text"
      inputMode="decimal"
      ref={ref}
    />
  );
});

CurrencyInput.displayName = 'CurrencyInput';
